import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CustomComponent, IBakeryCountry, ngHelper, POST_NUMBER_LENGTHS } from '@cakeiteasy/web-utils';
import { Subject, firstValueFrom } from 'rxjs';
import { CitiesApiService } from '../../../api-services/services/cities-api.service';
import { DeliveryApiService } from '../../../api-services/services/delivery-api.service';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { ICustomDeliveryTime } from '../../../cart/models/delivery/i-cart-delivery-info';

@Component({
  standalone: false,
  selector: 'bakery-delivery-check',
  templateUrl: './bakery-delivery-check.component.html'
})
export class BakeryDeliveryCheckComponent extends CustomComponent implements OnChanges {
  @Input() bakeryId: number;
  @Input() country: IBakeryCountry;
  @Input() showNext: boolean;
  @Input() delivery: any;

  @Output() onNext = new EventEmitter<any>();

  public NOT_FOUND = 'Not found';

  public postNumber: string;
  public deliveryPrices: any[];
  public onPostNumberUpdated$ = new Subject<string>();
  public cityName: string;
  public busy: Promise<any>;
  public isDeliveryPricesReady: boolean;
  public cityLocation: any;
  public deliveryScheduleOverrides: ICustomDeliveryTime[];

  constructor(private _deliveryApiService: DeliveryApiService,
              private _cityService: CitiesApiService,
              private _translate: TranslateService) {
    super();

    this.NOT_FOUND = this._translate.instant('common.notFound');
  }

  ngOnChanges(changes: SimpleChanges): void {
    ngHelper.ifChanged(changes, 'bakeryId', (bakeryId) => {
      this._subscribe();

      if (bakeryId && this.postNumber) {
        this.onPostNumberUpdated$.next(this.postNumber);
      }
    });

    ngHelper.ifChanged(changes, 'delivery', (delivery) => {
      if (delivery && this.bakeryId) {
        this.postNumber = delivery.postNumber;
        this.onPostNumberUpdated$.next(delivery.postNumber);
      }
    });
  }

  private _subscribe() {
    this.unsubscribeAll();
    this.addSubscriber(this.onPostNumberUpdated$
      .pipe(map((postNumber) => {
        if (!postNumber) {
          this.cityName = null;
          this.deliveryPrices = null;
          this.deliveryScheduleOverrides = null;
        }

        return postNumber;
      }))
      .pipe(debounceTime(350))
      .pipe(distinctUntilChanged())
      .pipe(filter((postNumber) => (Boolean(postNumber) && this.checkPostNumberLength(postNumber))))
      .subscribe((postNumber) => {
        this.busy = Promise.all([
          this._loadDeliveryPrice(this.bakeryId, postNumber),
          this._searchLocation(postNumber),
          this.loadDeliveryScheduleOverrides(this.bakeryId, postNumber),
        ]);
      }));
  }

  public checkPostNumberLength(postNumber: string) {
    return postNumber.length === POST_NUMBER_LENGTHS[this.country.code];
  }

  private async _searchLocation(postNumber: string) {
    const location = await this._cityService.searchLocationByPostNumber(this.country.code, postNumber);
    if (location) {
      this.cityLocation = location;
      this.cityName = location.placeName || location.cityName;
    } else {
      this.cityName = this._translate.instant('common.notFound');
    }
  }

  private async _loadDeliveryPrice(bakeryId: number, postNumber: string) {
    this.isDeliveryPricesReady = false;

    try {
      this.deliveryPrices = await firstValueFrom(this._deliveryApiService.getBakeryDeliveryPrice(bakeryId, postNumber));
      this.isDeliveryPricesReady = true;
    } catch (e) {
      this.deliveryPrices = null;
      this.isDeliveryPricesReady = true;
    }
  }

  private async loadDeliveryScheduleOverrides(bakeryId: number, postNumber: string) {
    this.deliveryScheduleOverrides = null;

    try {
      this.deliveryScheduleOverrides = await firstValueFrom(this._deliveryApiService.getDeliveryScheduleOverrides(bakeryId, postNumber));
    } catch (e) {
      console.error('Error while loading custom delivery times');
    }
  }

  public onPostNumberUpdated(postNumber: string) {
    this.onPostNumberUpdated$.next(postNumber);
  }
}
